// client/src/coms/views/SocialView.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { comsApiDelete, comsApiGet, comsApiPatch, comsApiPost } from '../comsApi';
import ChatViewScrollbarStyle from './chatview_parts/ChatViewScrollbarStyle';
import ChatComposer from './chatview_parts/ChatComposer';
import ChatMessageList from './chatview_parts/ChatMessageList';
import useChatEmojiPopoverClose from './chatview_parts/useChatEmojiPopoverClose';
import useChatDerivedMessages from './chatview_parts/useChatDerivedMessages';
import { buildSharedMessageActions } from './chatview_parts/messageActions';
import { saveBookmark } from '../organisation/bookmarkActions';
import { openArea } from '../organisation/orgShared';
import { captureScroll, readTabMemory, restoreScroll, writeTabMemory } from '../workspace/tabMemory';
import { markEngaged } from '../workspace/engagement';
import DmEncryptionBar from '../e2ee/DmEncryptionBar';
import EncryptedReportDialog from '../e2ee/EncryptedReportDialog';
import { withEncryptedView } from '../e2ee/EncryptedMessageView';
import {
  acceptContactChange,
  contactIdentity,
  verifyContact,
  cryptoAvailable,
  decryptedResult,
  decryptMessages,
  forgetFailedResults,
  onE2eeChange,
  prepareThread,
  retryThread,
  sendEncryptedDm,
  threadSession,
} from '../e2ee/cryptoSession';
import {
  normalizeUploadList,
  composeOutgoingMessageBodyWithUploads,
  uploadFilesToComms,
  mergeMessageContentAndAttachments,
} from './chatview_parts/chatUploadUtils';

// Unsent DM text, per account and thread (switching account on this device never shows another's draft).
function dmDraftKey(userId, threadId) {
  const uid = Number(userId) > 0 ? Math.floor(Number(userId)) : 0;
  const tid = String(threadId || '').trim();
  return uid && tid ? `voxemera:dmdraft:u${uid}:${tid}` : '';
}

function readDmDraft(userId, threadId) {
  try {
    const k = dmDraftKey(userId, threadId);
    return k ? String(window.localStorage.getItem(k) || '') : '';
  } catch {
    return '';
  }
}

function writeDmDraft(userId, threadId, value) {
  try {
    const k = dmDraftKey(userId, threadId);
    if (!k) return;
    if (value) window.localStorage.setItem(k, String(value));
    else window.localStorage.removeItem(k);
  } catch {}
}

// For tests (DM drafts per account and thread).
export { dmDraftKey, readDmDraft, writeDmDraft };

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function safeObj(v) {
  return v && typeof v === 'object' ? v : {};
}

function toInt(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.floor(n) : fallback;
}

function avatarUrlForUserId(userId) {
  const id = toInt(userId, 0);
  if (!id) return '';
  return `/api/coms/avatar/${encodeURIComponent(String(id))}`;
}

function isGenericUserLabel(value, userId) {
  const text = String(value || '').trim();
  if (!text) return true;
  const uid = toInt(userId, 0);
  const compact = text.toLowerCase().replace(/\s+/g, ' ');
  if (compact === 'user' || compact === 'unknown user') return true;
  if (/^user\s*#?\??$/.test(compact)) return true;
  if (uid > 0 && compact === `user ${uid}`) return true;
  if (uid > 0 && compact === `user #${uid}`) return true;
  return false;
}

function pickUserLabel(userObj, userId, serverMemberNameMap) {
  const userData = safeObj(userObj);
  const uid = toInt(userId != null ? userId : userData.id != null ? userData.id : userData.user_id, 0);
  const serverName = uid > 0 ? String(safeObj(serverMemberNameMap)[String(uid)] || '').trim() : '';
  const candidates = [
    userData.display_name,
    userData.displayName,
    userData.name,
    userData.label,
    userData.username,
    userData.email,
    serverName,
  ];

  for (const candidate of candidates) {
    const label = String(candidate || '').trim();
    if (label && !isGenericUserLabel(label, uid)) return label;
  }

  if (serverName) return serverName;
  return `User ${uid || '?'}`;
}

function buildServerMemberNameMap(server) {
  const out = {};
  const members = safeArray(server && server.members);

  for (const raw of members) {
    const m = safeObj(raw);
    const uid = toInt(m.id != null ? m.id : m.user_id != null ? m.user_id : m.userId, 0);
    if (!uid) continue;

    const displayName = String(m.display_name || '').trim();
    const username = String(m.username || '').trim();
    const name = String(m.name || '').trim();
    const best = displayName || username || name;

    if (best) out[String(uid)] = best;
  }

  return out;
}

function panelStyle() {
  return {
    background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
    borderRadius: 16,
    padding: 16,
    minHeight: 0,
    boxSizing: 'border-box',
  };
}

function titleStyle() {
  return {
    margin: 0,
    fontSize: 18,
    fontWeight: 800,
    color: 'var(--vx-text, #fff)',
  };
}

function textStyle() {
  return {
    margin: 0,
    fontSize: 14,
    lineHeight: 1.45,
    color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.74 * var(--vx-ov, 1)))',
  };
}

function actionBtnStyle(kind) {
  return {
    appearance: 'none',
    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
    background: kind === 'primary' ? 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
    color: 'var(--vx-text, #fff)',
    padding: '7px 11px',
    borderRadius: 10,
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: 12,
  };
}

function sectionHeader(label, sub) {
  return (
    <div style={{ display: 'grid', gap: 4, marginBottom: 12 }}>
      <h2 style={titleStyle()}>{label}</h2>
      {sub ? <p style={textStyle()}>{sub}</p> : null}
    </div>
  );
}

function normalizeFriend(item, serverMemberNameMap) {
  const o = safeObj(item);
  const other = safeObj(o.other_user);
  const otherUserId = toInt(o.other_user_id != null ? o.other_user_id : other.id, 0);
  const username = pickUserLabel({ ...other, username: o.username || other.username, label: o.label || other.label }, otherUserId, serverMemberNameMap);

  return {
    id: toInt(o.id, 0),
    other_user_id: otherUserId,
    username,
    requested_by: toInt(o.requested_by, 0),
    status: String(o.status || '').trim(),
    requested_at: o.requested_at ? String(o.requested_at) : '',
  };
}

function normalizePending(item, serverMemberNameMap) {
  const o = safeObj(item);
  const other = safeObj(o.other_user);
  const otherUserId = toInt(o.other_user_id != null ? o.other_user_id : other.id, 0);
  const username = pickUserLabel(other, otherUserId, serverMemberNameMap);
  const serverName = otherUserId > 0 ? String(safeObj(serverMemberNameMap)[String(otherUserId)] || '').trim() : '';

  return {
    id: toInt(o.id, 0),
    other_user_id: otherUserId,
    username,
    label: serverName || username,
    requested_by: toInt(o.requested_by, 0),
    status: String(o.status || '').trim(),
    requested_at: o.requested_at ? String(o.requested_at) : '',
  };
}

function normalizeThread(item, serverMemberNameMap) {
  const o = safeObj(item);
  const other = safeObj(o.other_user);
  const otherUserId = toInt(o.other_user_id != null ? o.other_user_id : other.id, 0);
  const username = pickUserLabel({ ...other, username: o.username || other.username, label: o.label || other.label }, otherUserId, serverMemberNameMap);

  return {
    id: toInt(o.id, 0),
    other_user_id: otherUserId,
    username,
    label: username,
    can_send: !!o.can_send,
    is_blocked: !!o.is_blocked,
    created_at: o.created_at ? String(o.created_at) : '',
    updated_at: o.updated_at ? String(o.updated_at) : '',
    last_message_at: o.last_message_at ? String(o.last_message_at) : '',
    has_unread: !!o.has_unread,
    latest_message_id: toInt(o.latest_message_id, 0),
    latest_message_sender_user_id: toInt(o.latest_message_sender_user_id, 0),
    latest_message_created_at: o.latest_message_created_at ? String(o.latest_message_created_at) : '',
    viewer_last_read_message_id: toInt(o.viewer_last_read_message_id, 0),
    viewer_last_read_at: o.viewer_last_read_at ? String(o.viewer_last_read_at) : '',
    other_user_last_read_message_id: toInt(o.other_user_last_read_message_id, 0),
    other_user_last_read_at: o.other_user_last_read_at ? String(o.other_user_last_read_at) : '',
    e2ee: o.e2ee && typeof o.e2ee === 'object' ? o.e2ee : null,
  };
}

function normalizeMessage(item, serverMemberNameMap) {
  const o = safeObj(item);
  const sender = safeObj(o.sender_user);
  const senderUserId = toInt(o.sender_user_id != null ? o.sender_user_id : sender.id, 0);

  return {
    id: toInt(o.id, 0),
    thread_id: toInt(o.thread_id, 0),
    sender_user_id: senderUserId,
    sender_username: pickUserLabel({ ...sender, username: o.sender_username || sender.username }, senderUserId, serverMemberNameMap),
    content: String(o.content || ''),
    attachments: normalizeUploadList(o.attachments),
    is_deleted: !!o.is_deleted,
    created_at: o.created_at ? String(o.created_at) : '',
    updated_at: o.updated_at ? String(o.updated_at) : '',
    can_edit: !!o.can_edit,
    can_delete: !!o.can_delete,
    seen_by_other_user: !!o.seen_by_other_user,
    // The encryption state the message was sent in, and the permanent encryption status notices.
    encryption: o.encryption === 'e2ee' ? 'e2ee' : 'standard',
    system_kind: o.system_kind ? String(o.system_kind) : '',
    system_data: o.system_data && typeof o.system_data === 'object' ? o.system_data : null,
    ciphertext: o.encryption === 'e2ee' && o.ciphertext ? String(o.ciphertext) : '',
    sender_device_id: o.sender_device_id ? String(o.sender_device_id) : '',
  };
}


function parseMessageTime(value) {
  const raw = String(value || '').trim();
  if (!raw) return 0;

  const direct = Date.parse(raw);
  if (Number.isFinite(direct)) return direct;

  const asUtc = raw.includes('T') ? Date.parse(`${raw}Z`) : Date.parse(`${raw.replace(' ', 'T')}Z`);
  if (Number.isFinite(asUtc)) return asUtc;

  return 0;
}

function getMessageSortValue(message) {
  const m = safeObj(message);
  const id = toInt(m.id, 0);
  if (id > 0) return id;

  const createdAt = parseMessageTime(m.created_at);
  if (createdAt > 0) return createdAt;

  const updatedAt = parseMessageTime(m.updated_at);
  if (updatedAt > 0) return updatedAt;

  return 0;
}

function sortDmMessages(rows) {
  const arr = safeArray(rows).slice();
  arr.sort((a, b) => {
    const av = getMessageSortValue(a);
    const bv = getMessageSortValue(b);
    if (av !== bv) return av - bv;

    const aid = String(a && a.id != null ? a.id : '');
    const bid = String(b && b.id != null ? b.id : '');
    if (aid < bid) return -1;
    if (aid > bid) return 1;
    return 0;
  });
  return arr;
}

function mergeDmMessageRows(previousRows, incomingRows) {
  const byId = new Map();
  const withoutId = [];

  for (const row of safeArray(previousRows)) {
    const msg = normalizeMessage(row);
    const id = toInt(msg.id, 0);
    if (id > 0) byId.set(String(id), msg);
    else withoutId.push(msg);
  }

  for (const row of safeArray(incomingRows)) {
    const msg = normalizeMessage(row);
    const id = toInt(msg.id, 0);
    if (id > 0) {
      const prev = byId.get(String(id));
      byId.set(String(id), normalizeMessage({ ...safeObj(prev), ...safeObj(msg) }));
    } else {
      withoutId.push(msg);
    }
  }

  return sortDmMessages([...withoutId, ...Array.from(byId.values())]);
}

function buildNoCachePath(path) {
  const base = String(path || '');
  if (!base) return base;
  const join = base.includes('?') ? '&' : '?';
  return `${base}${join}_=${Date.now()}`;
}

function isMobileViewport() {
  try {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return !!window.matchMedia('(max-width: 900px)').matches;
  } catch {
    return false;
  }
}

function normalizeQuoteMessage(msg) {
  const m = msg && typeof msg === 'object' ? msg : {};
  const author = String(m.author || m.sender_username || '').trim();
  const stamp = String(m.stamp || m.created_at || '').trim();
  const header = author && stamp ? `${author} • ${stamp}` : author || stamp || 'Message';
  const text = String(m.text || m.content || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  return { author, stamp, header, text };
}

function buildQuoteBlockFromPending(pq) {
  const q = pq && typeof pq === 'object' ? pq : null;
  if (!q) return '';

  const header = String(q.header || '').trim() || 'Message';
  const rawText = String(q.text || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = rawText.split('\n');
  const quotedLines = lines.map((l) => `> ${l}`.replace(/^> $/, '>')).join('\n');
  return `> ${header}\n${quotedLines}\n\n`;
}

function mapDmMessageToChatRaw(msg, serverMemberNameMap) {
  const m = safeObj(msg);
  const mergedBody = mergeMessageContentAndAttachments(m.content, m.attachments);

  return {
    id: m.id != null ? String(m.id) : '',
    body: mergedBody,
    user_id: toInt(m.sender_user_id, 0),
    username: pickUserLabel({ username: m.sender_username }, toInt(m.sender_user_id, 0), serverMemberNameMap),
    created_at: m.created_at ? String(m.created_at) : '',
    edited_at: !m.is_deleted && m.updated_at ? String(m.updated_at) : '',
    deleted_at: m.is_deleted ? String(m.updated_at || m.created_at || '1') : '',
    avatar_url: avatarUrlForUserId(toInt(m.sender_user_id, 0)),
    system_kind: m.system_kind || '',
    encryption: m.encryption || 'standard',
    e2ee_view: m.e2ee_view || null,
    _dm: {
      can_edit: !!m.can_edit,
      can_delete: !!m.can_delete,
      seen_by_other_user: !!m.seen_by_other_user,
      raw: m,
    },
  };
}

function AvatarVisual({
  userId,
  label,
  size = 32,
  background = 'rgba(255,255,255,0.08)',
  border = 'none',
  fontSize = 12,
  fontWeight = 800,
  textColor = 'rgba(255,255,255,0.85)',
}) {
  const [broken, setBroken] = useState(false);
  const avatarUrl = avatarUrlForUserId(userId);
  const initial = String(label || '?').slice(0, 1).toUpperCase();

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        overflow: 'hidden',
        flex: '0 0 auto',
        background,
        border,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {avatarUrl && !broken ? (
        <img
          src={avatarUrl}
          alt=""
          aria-hidden="true"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          onError={() => setBroken(true)}
        />
      ) : (
        <span style={{ fontSize, fontWeight, color: textColor }}>
          {initial}
        </span>
      )}
    </div>
  );
}

function FriendRow({ friend, onOpenChat }) {
  return (
    <button
      type="button"
      onClick={() => onOpenChat(friend)}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 12px',
        borderRadius: 12,
        background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
        border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
        color: 'var(--vx-text, #fff)',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <AvatarVisual
        userId={friend.other_user_id}
        label={friend.username}
        size={34}
        background="rgba(255,255,255,0.08)"
        border="none"
        fontSize={14}
      />

      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: 'var(--vx-text, #fff)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {friend.username}
        </div>
        <div style={{ fontSize: 12, color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.55 * var(--vx-ov, 1)))' }}>Open direct message</div>
      </div>
    </button>
  );
}

function PendingRow({ item, type, onAccept, onDecline }) {
  const displayLabel = item.label || item.username;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 12px',
        borderRadius: 12,
        background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
        border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
      }}
    >
      <AvatarVisual
        userId={item.other_user_id}
        label={displayLabel}
        size={34}
        background="rgba(255,255,255,0.08)"
        border="none"
        fontSize={14}
      />

      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: 'var(--vx-text, #fff)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {displayLabel}
        </div>
        <div style={{ fontSize: 12, color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.55 * var(--vx-ov, 1)))' }}>
          {type === 'incoming' ? 'Incoming request' : 'Outgoing request'}
        </div>
      </div>

      {type === 'incoming' ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={actionBtnStyle('primary')} onClick={() => onAccept(item.other_user_id)}>
            Accept
          </button>
          <button type="button" style={actionBtnStyle('ghost')} onClick={() => onDecline(item.other_user_id)}>
            Decline
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default function SocialView({
  server,
  user,
  socialSection = 'dms',
  onSelectSection,
  selectedThreadId,
  onSelectThreadId,
}) {
  const serverMemberNameMap = useMemo(() => buildServerMemberNameMap(server), [server]);
  const [isShellMobile, setIsShellMobile] = useState(() => isMobileViewport());

  const [friends, setFriends] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [threads, setThreads] = useState([]);
  const [thread, setThread] = useState(null);

  const [draft, setDraft] = useState('');
  const [messages, setMessages] = useState([]);
  const [loadErr, setLoadErr] = useState('');
  const [messageActionErr, setMessageActionErr] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingUploads, setPendingUploads] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [pendingQuote, setPendingQuote] = useState(null);

  const [editingMessageId, setEditingMessageId] = useState('');
  const [editDraft, setEditDraft] = useState('');
  const [editBusyMessageId, setEditBusyMessageId] = useState('');
  const [deleteBusyMessageId, setDeleteBusyMessageId] = useState('');
  const [hoveredMessageId, setHoveredMessageId] = useState('');

  const [currentReadState, setCurrentReadState] = useState(null);

  const scrollerRef = useRef(null);
  const messagesContentRef = useRef(null);
  const inputRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const emojiPopoverRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const lastMarkedReadMessageIdRef = useRef(0);
  const markReadTimerRef = useRef(null);
  const threadLoadSeqRef = useRef(0);
  const refreshListsBusyRef = useRef(false);
  const activeThreadPollDelayRef = useRef(900);

  const myUserId = useMemo(() => {
    const u = safeObj(user);
    return toInt(u.id != null ? u.id : u.user_id, 0);
  }, [user]);

  // Draft and scroll position per thread survive switching tabs and reloads (draft) or tabs (scroll).
  const dmMemoryKey = selectedThreadId ? `d:${selectedThreadId}` : '';
  const hasDmMessages = messages.length > 0;
  // The draft in state belongs to a thread only once the value loaded for it has been applied, so switching
  // threads never writes one thread's text under another thread's key.
  const draftLoadRef = useRef({ tid: '', value: '', applied: false });
  useEffect(() => {
    const tid = String(selectedThreadId || '');
    const value = readDmDraft(myUserId, tid);
    draftLoadRef.current = { tid, value, applied: false };
    setDraft(value);
  }, [selectedThreadId, myUserId]);
  useEffect(() => {
    const loadState = draftLoadRef.current;
    if (loadState.tid !== String(selectedThreadId || '')) return;
    if (!loadState.applied) {
      if (draft === loadState.value) loadState.applied = true;
      return;
    }
    writeDmDraft(myUserId, selectedThreadId, draft);
    // Writing keeps this conversation's tab (only edits get here, never the draft restored on opening).
    if (String(draft || '').trim() && selectedThreadId) markEngaged(`d:${selectedThreadId}`);
  }, [draft, selectedThreadId, myUserId]);
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || !dmMemoryKey) return undefined;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        writeTabMemory(myUserId, dmMemoryKey, { scroll: captureScroll(el) });
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [dmMemoryKey, myUserId, hasDmMessages]);
  const dmRestoredRef = useRef('');
  useEffect(() => {
    if (!dmMemoryKey || !messages.length || dmRestoredRef.current === dmMemoryKey) return;
    dmRestoredRef.current = dmMemoryKey;
    const saved = readTabMemory(myUserId, dmMemoryKey);
    if (!saved || !saved.scroll || saved.scroll.atBottom) return;
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if (restoreScroll(scrollerRef.current, saved.scroll)) stickToBottomRef.current = false;
      })
    );
  }, [dmMemoryKey, messages.length, myUserId]);

  const selectedThread = useMemo(
    () => threads.find((x) => String(x.id) === String(selectedThreadId || '')) || null,
    [threads, selectedThreadId]
  );

  const activeThread = useMemo(() => {
    if (selectedThread && selectedThread.id) return selectedThread;
    if (thread && thread.id) return normalizeThread(thread, serverMemberNameMap);
    return null;
  }, [selectedThread, thread, serverMemberNameMap]);

  // The freshest encryption state: the open thread's own load, else the conversation list's copy.
  const threadE2ee = useMemo(() => {
    if (thread && thread.id && String(thread.id) === String(selectedThreadId || '') && thread.e2ee) return thread.e2ee;
    return (activeThread && activeThread.e2ee) || null;
  }, [thread, activeThread, selectedThreadId]);
  // After an encryption downgrade, sending stays paused until the change is acknowledged.
  const e2eePendingAck = !!(threadE2ee && threadE2ee.pending_ack);

  // Encrypted mode (server) and whether this device has really set the session up (the only source of a lock).
  const [e2eeTick, setE2eeTick] = useState(0);
  useEffect(() => onE2eeChange(() => setE2eeTick((n) => n + 1)), []);
  const e2eeActive = !!(threadE2ee && threadE2ee.status === 'e2ee');
  const e2eeLocal = useMemo(() => {
    if (!e2eeActive) return null;
    if (!cryptoAvailable()) return { state: 'unsupported', error: '' };
    return threadSession(selectedThreadId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [e2eeActive, selectedThreadId, e2eeTick]);
  useEffect(() => {
    if (e2eeActive && selectedThreadId && cryptoAvailable()) prepareThread(selectedThreadId, threadE2ee);
  }, [e2eeActive, selectedThreadId, threadE2ee]);
  const e2eeBlocked = e2eeActive && !(e2eeLocal && e2eeLocal.state === 'ready');

  // The other participant's identity as this device sees it: safety codes, verified or not.
  const [e2eeContact, setE2eeContact] = useState(null);
  const [e2eeReportId, setE2eeReportId] = useState(null);
  // A send refused for a reason that lasts (e.g. a moderation suspension); kept until a send succeeds.
  const [dmSendBlocked, setDmSendBlocked] = useState('');
  const e2eeRoom = (threadE2ee && threadE2ee.room_id) || '';
  const e2eeLocalKey = e2eeLocal ? `${e2eeLocal.state}:${e2eeLocal.code || ''}` : '';
  useEffect(() => {
    const m = threadE2ee && threadE2ee.members;
    if (!e2eeActive || !m || !/^(ready|failed:identity_changed)/.test(e2eeLocalKey)) {
      setE2eeContact(null);
      return undefined;
    }
    let alive = true;
    contactIdentity(m[0], m[1])
      .then((c) => alive && setE2eeContact({ verified: !!(c.theirs && c.theirs.verified), mine: c.mine, theirs: c.theirs }))
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [e2eeActive, e2eeRoom, e2eeLocalKey, e2eeTick]);

  const canType = !!activeThread && !!activeThread.can_send && !e2eePendingAck && !e2eeBlocked;

  // Decrypts encrypted messages on this device (old ones too, after a downgrade); failed ones are retried
  // every 15 s in case their room key arrives late.
  const [decryptTick, setDecryptTick] = useState(0);
  const [decryptRetry, setDecryptRetry] = useState(0);
  useEffect(() => {
    if (!threadE2ee || !threadE2ee.room_id || !messages.some((m) => m.encryption === 'e2ee')) return undefined;
    const server = threadE2ee.room_id.split(':').slice(1).join(':');
    decryptMessages(threadE2ee, messages, (uid) => `@u${uid}:${server}`, { allowNewDevice: false }).then((changed) => {
      // Redraw even if this effect re-ran meanwhile: the results changed, and the next pass would not report it.
      if (changed) setDecryptTick((n) => n + 1);
    });
    return undefined;
  }, [messages, threadE2ee, decryptRetry, e2eeTick]);
  useEffect(() => {
    if (!threadE2ee || !threadE2ee.room_id) return undefined;
    const t = setInterval(() => {
      forgetFailedResults();
      setDecryptRetry((n) => n + 1);
    }, 15000);
    return () => clearInterval(t);
  }, [threadE2ee]);

  useChatEmojiPopoverClose({
    showEmojiPicker,
    setShowEmojiPicker,
    emojiButtonRef,
    emojiPopoverRef,
  });

  const refreshLists = useCallback(async () => {
    if (refreshListsBusyRef.current) return;

    refreshListsBusyRef.current = true;

    try {
      const [friendsRes, reqRes, dmsRes] = await Promise.all([
        comsApiGet(buildNoCachePath('/friends')).catch(() => null),
        comsApiGet(buildNoCachePath('/friend-requests')).catch(() => null),
        comsApiGet(buildNoCachePath('/dms')).catch(() => null),
      ]);

      const nextFriends = safeArray(friendsRes && friendsRes.friends).map((item) => normalizeFriend(item, serverMemberNameMap));
      const nextIncoming = safeArray(reqRes && reqRes.incoming).map((item) => normalizePending(item, serverMemberNameMap));
      const nextOutgoing = safeArray(reqRes && reqRes.outgoing).map((item) => normalizePending(item, serverMemberNameMap));
      const nextThreads = safeArray(dmsRes && dmsRes.threads).map((item) => normalizeThread(item, serverMemberNameMap));

      setFriends(nextFriends);
      setIncoming(nextIncoming);
      setOutgoing(nextOutgoing);
      setThreads(nextThreads);

      const selectedId = String(selectedThreadId || '').trim();
      const selectedStillExists = selectedId && nextThreads.some((item) => String(item.id) === selectedId);

      if (selectedId && !selectedStillExists) {
        setThread(null);
        setMessages([]);
        setCurrentReadState(null);
        setLoadErr('');
        if (typeof onSelectThreadId === 'function') onSelectThreadId('');
        if (typeof onSelectSection === 'function') onSelectSection(nextFriends.length ? 'friends' : 'dms');
      }
    } finally {
      refreshListsBusyRef.current = false;
    }
  }, [serverMemberNameMap, selectedThreadId, onSelectThreadId, onSelectSection]);

  const loadCurrentThread = useCallback(async ({ replace = false } = {}) => {
    const tid = String(selectedThreadId || '').trim();
    const seq = threadLoadSeqRef.current + 1;
    threadLoadSeqRef.current = seq;

    if (!tid) {
      setThread(null);
      setMessages([]);
      setCurrentReadState(null);
      return;
    }

    const path = buildNoCachePath(`/dms/thread/${encodeURIComponent(tid)}/messages`);
    const res = await comsApiGet(path);

    if (threadLoadSeqRef.current !== seq) return;

    const nextThread = normalizeThread(safeObj(res && res.thread), serverMemberNameMap);
    const nextMessages = safeArray(res && res.messages).map((item) => normalizeMessage(item, serverMemberNameMap));

    setThread(nextThread);
    setMessages((prev) => (replace ? sortDmMessages(nextMessages) : mergeDmMessageRows(prev, nextMessages)));
    setCurrentReadState(safeObj(res && res.read_state));
  }, [selectedThreadId]);

  const markCurrentThreadRead = useCallback(async (messageId, { immediate = false } = {}) => {
    const tid = String(selectedThreadId || '').trim();
    const mid = toInt(messageId, 0);

    if (!tid || !mid || socialSection !== 'dms') return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    if (lastMarkedReadMessageIdRef.current >= mid) return;

    const run = async () => {
      try {
        const res = await comsApiPost(`/dms/thread/${encodeURIComponent(tid)}/read`, {
          message_id: mid,
        });

        lastMarkedReadMessageIdRef.current = mid;

        const nextThread = normalizeThread(safeObj(res && res.thread), serverMemberNameMap);
        const nextReadState = safeObj(res && res.read_state);

        setThread((prev) => {
          const merged = { ...(safeObj(prev)), ...(safeObj(nextThread)) };
          return normalizeThread(merged, serverMemberNameMap);
        });

        setThreads((prev) =>
          safeArray(prev).map((item) =>
            String(item.id) === String(tid)
              ? normalizeThread({ ...safeObj(item), ...safeObj(nextThread), has_unread: false, viewer_last_read_message_id: mid }, serverMemberNameMap)
              : item
          )
        );

        setCurrentReadState((prev) => ({
          ...safeObj(prev),
          ...nextReadState,
        }));
      } catch {}
    };

    if (immediate) {
      if (markReadTimerRef.current) {
        clearTimeout(markReadTimerRef.current);
        markReadTimerRef.current = null;
      }
      run().catch(() => {});
      return;
    }

    if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current);
    markReadTimerRef.current = setTimeout(() => {
      markReadTimerRef.current = null;
      run().catch(() => {});
    }, 700);
  }, [selectedThreadId, socialSection]);

  useEffect(() => {
    function onResize() {
      setIsShellMobile(isMobileViewport());
    }

    onResize();

    if (typeof window === 'undefined') return undefined;

    const vv = window.visualViewport || null;

    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    if (vv) {
      vv.addEventListener('resize', onResize);
      vv.addEventListener('scroll', onResize);
    }

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      if (vv) {
        vv.removeEventListener('resize', onResize);
        vv.removeEventListener('scroll', onResize);
      }
    };
  }, []);

  useEffect(() => {
    let alive = true;
    let timer = null;

    async function load() {
      try {
        setLoadErr('');
        await refreshLists();
      } catch (e) {
        if (!alive) return;
        setLoadErr(String((e && e.message) || e));
      }
    }

    load().catch(() => {});
    timer = setInterval(() => {
      load().catch(() => {});
    }, 5000);

    return () => {
      alive = false;
      if (timer) clearInterval(timer);
    };
  }, [refreshLists]);

  useEffect(() => {
    let alive = true;
    let timer = null;

    async function loadThreadMessages({ replace = false } = {}) {
      const tid = String(selectedThreadId || '').trim();
      if (!tid || socialSection !== 'dms') {
        if (!alive) return;
        threadLoadSeqRef.current += 1;
        setThread(null);
        setMessages([]);
        setCurrentReadState(null);
        return;
      }

      try {
        setLoadErr('');
        await loadCurrentThread({ replace });
        activeThreadPollDelayRef.current = 900;
      } catch (e) {
        if (!alive) return;

        const message = String((e && e.message) || e || '');
        const missingThread = /thread not found|\b404\b/i.test(message);

        if (missingThread) {
          threadLoadSeqRef.current += 1;
          setThread(null);
          setMessages([]);
          setCurrentReadState(null);
          setLoadErr('');
          if (typeof onSelectThreadId === 'function') onSelectThreadId('');
          if (typeof onSelectSection === 'function') onSelectSection(friends.length ? 'friends' : 'dms');
          return;
        }

        activeThreadPollDelayRef.current = Math.min(4000, Math.max(1200, activeThreadPollDelayRef.current * 1.5));
        setLoadErr(message);
      }
    }

    function queueNextPoll() {
      if (!alive) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        await loadThreadMessages().catch(() => {});
        queueNextPoll();
      }, activeThreadPollDelayRef.current);
    }

    activeThreadPollDelayRef.current = 900;
    loadThreadMessages({ replace: true }).catch(() => {});

    if (socialSection === 'dms' && String(selectedThreadId || '').trim()) {
      queueNextPoll();
    }

    return () => {
      alive = false;
      threadLoadSeqRef.current += 1;
      if (timer) clearTimeout(timer);
    };
  }, [selectedThreadId, socialSection, loadCurrentThread, onSelectThreadId, onSelectSection, friends.length]);

  useEffect(() => {
    setDmSendBlocked('');
    setShowEmojiPicker(false);
    setPendingQuote(null);
    setPendingUploads([]);
    setEditingMessageId('');
    setEditDraft('');
    setHoveredMessageId('');
    setMessageActionErr('');
    lastMarkedReadMessageIdRef.current = 0;
    if (markReadTimerRef.current) {
      clearTimeout(markReadTimerRef.current);
      markReadTimerRef.current = null;
    }
  }, [selectedThreadId]);

  const maybeAutoScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  useEffect(() => {
    const target = messagesContentRef.current;
    if (!target) return;
    if (typeof ResizeObserver === 'undefined') return;

    let rafA = 0;
    let rafB = 0;

    const ro = new ResizeObserver(() => {
      if (rafA) cancelAnimationFrame(rafA);
      if (rafB) cancelAnimationFrame(rafB);

      rafA = requestAnimationFrame(() => {
        maybeAutoScroll();
        rafB = requestAnimationFrame(() => {
          maybeAutoScroll();
        });
      });
    });

    try {
      ro.observe(target);
    } catch {}

    return () => {
      try {
        ro.disconnect();
      } catch {}
      if (rafA) cancelAnimationFrame(rafA);
      if (rafB) cancelAnimationFrame(rafB);
    };
  }, [maybeAutoScroll]);

  useEffect(() => {
    setTimeout(() => maybeAutoScroll(), 0);
  }, [messages.length, maybeAutoScroll]);

  useEffect(() => {
    if (socialSection !== 'dms') return;
    const tid = String(selectedThreadId || '').trim();
    if (!tid) return;
    if (!messages.length) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;

    const lastMessage = messages[messages.length - 1];
    const lastMessageId = toInt(lastMessage && lastMessage.id, 0);
    if (!lastMessageId) return;

    const el = scrollerRef.current;
    if (!el) return;

    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining > 80) return;

    markCurrentThreadRead(lastMessageId).catch(() => {});
  }, [messages, selectedThreadId, socialSection, markCurrentThreadRead]);

  useEffect(() => {
    if (socialSection !== 'dms') return;

    function handleVisibility() {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      const lastMessage = messages.length ? messages[messages.length - 1] : null;
      const lastMessageId = toInt(lastMessage && lastMessage.id, 0);
      if (!lastMessageId) return;

      const el = scrollerRef.current;
      if (!el) return;
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (remaining > 80) return;

      markCurrentThreadRead(lastMessageId, { immediate: true }).catch(() => {});
    }

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibility);
    }

    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibility);
      }
    };
  }, [messages, socialSection, markCurrentThreadRead]);

  useEffect(() => {
    return () => {
      if (markReadTimerRef.current) {
        clearTimeout(markReadTimerRef.current);
        markReadTimerRef.current = null;
      }
    };
  }, []);


  useEffect(() => {
    if (socialSection !== 'dms') return undefined;
    if (!String(selectedThreadId || '').trim()) return undefined;
    if (typeof window === 'undefined') return undefined;

    let busy = false;

    const refreshOpenThreadNow = async () => {
      if (busy) return;
      busy = true;
      try {
        await Promise.all([
          loadCurrentThread(),
          refreshLists(),
        ]);
      } catch {
      } finally {
        busy = false;
      }
    };

    const onVisibility = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      refreshOpenThreadNow().catch(() => {});
    };

    window.addEventListener('focus', refreshOpenThreadNow);
    window.addEventListener('online', refreshOpenThreadNow);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }

    return () => {
      window.removeEventListener('focus', refreshOpenThreadNow);
      window.removeEventListener('online', refreshOpenThreadNow);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
  }, [selectedThreadId, socialSection, loadCurrentThread, refreshLists]);

  function handleScrollerScroll() {
    const el = scrollerRef.current;
    if (!el) return;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = remaining < 80;

    if (
      socialSection === 'dms' &&
      String(selectedThreadId || '').trim() &&
      remaining < 80 &&
      typeof document !== 'undefined' &&
      document.visibilityState === 'visible'
    ) {
      const lastMessage = messages.length ? messages[messages.length - 1] : null;
      const lastMessageId = toInt(lastMessage && lastMessage.id, 0);
      if (lastMessageId) {
        markCurrentThreadRead(lastMessageId).catch(() => {});
      }
    }
  }

  function insertIntoDraftAtCursor(value) {
    const textToInsert = String(value || '');
    if (!textToInsert) return;

    const el = inputRef.current;
    if (!el) {
      setDraft((prev) => `${prev || ''}${textToInsert}`);
      return;
    }

    const start = typeof el.selectionStart === 'number' ? el.selectionStart : String(draft || '').length;
    const end = typeof el.selectionEnd === 'number' ? el.selectionEnd : String(draft || '').length;
    const current = String(draft || '');
    const next = current.slice(0, start) + textToInsert + current.slice(end);

    setDraft(next);

    requestAnimationFrame(() => {
      try {
        el.focus();
        const pos = start + textToInsert.length;
        el.setSelectionRange(pos, pos);
      } catch {}
    });
  }

  function quoteMessage(msg) {
    const q = normalizeQuoteMessage(msg);
    setPendingQuote(q);
    try {
      const el = inputRef.current;
      if (el && typeof el.focus === 'function') el.focus();
    } catch {}
  }

  function clearPendingQuote() {
    setPendingQuote(null);
    try {
      const el = inputRef.current;
      if (el && typeof el.focus === 'function') el.focus();
    } catch {}
  }

  async function handlePickFiles(fileList) {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;

    try {
      setUploading(true);
      setLoadErr('');
      const uploaded = await uploadFilesToComms(files);
      setPendingUploads((prev) => [...prev, ...uploaded]);
    } catch (e) {
      setLoadErr(String((e && e.message) || e));
    } finally {
      setUploading(false);
    }
  }

  function removePendingUpload(uploadId) {
    const id = toInt(uploadId, 0);
    if (!id) return;
    setPendingUploads((prev) => prev.filter((item) => toInt(item && item.id, 0) !== id));
  }

  function clearPendingUploads() {
    setPendingUploads([]);
  }

  async function openFriendChat(friend) {
    const otherUserId = toInt(friend && friend.other_user_id, 0);
    if (!otherUserId) return;

    try {
      setLoadErr('');
      const res = await comsApiPost('/dms/thread', { user_id: otherUserId });
      const nextThread = safeObj(res && res.thread);
      const nextThreadId = toInt(nextThread && nextThread.id, 0);
      if (!nextThreadId) return;

      await refreshLists();

      if (typeof onSelectSection === 'function') onSelectSection('dms');
      if (typeof onSelectThreadId === 'function') onSelectThreadId(String(nextThreadId));
    } catch (e) {
      setLoadErr(String((e && e.message) || e));
    }
  }

  async function sendMessage({ closeEmojiPicker } = {}) {
    const tid = String(selectedThreadId || '').trim();
    const body = composeOutgoingMessageBodyWithUploads(draft, pendingQuote, pendingUploads, buildQuoteBlockFromPending);
    const uploadIds = safeArray(pendingUploads).map((item) => toInt(item && item.id, 0)).filter((v) => v > 0);

    if (!tid || (!body && !uploadIds.length) || sending || uploading || !canType) return;
    markEngaged(`d:${tid}`);

    try {
      setSending(true);
      setLoadErr('');
      let res;
      if (e2eeActive) {
        // Encrypted on this device; only ciphertext leaves it. Nothing is sent if encryption fails.
        if (uploadIds.length) throw new Error('Attachments cannot be sent in end-to-end encrypted conversations yet.');
        res = await sendEncryptedDm(tid, threadE2ee, body);
      } else {
        res = await comsApiPost(`/dms/thread/${encodeURIComponent(tid)}/messages`, {
          content: body,
          upload_ids: uploadIds,
        });
      }
      const next = normalizeMessage(safeObj(res && res.message), serverMemberNameMap);
      setDmSendBlocked('');

      setMessages((prev) => mergeDmMessageRows(prev, [next]));
      setDraft('');
      setPendingQuote(null);
      setPendingUploads([]);
      setShowEmojiPicker(false);

      if (typeof closeEmojiPicker === 'function') closeEmojiPicker();

      await Promise.all([
        refreshLists(),
        loadCurrentThread(),
      ]);

      requestAnimationFrame(() => {
        stickToBottomRef.current = true;
        maybeAutoScroll();
      });
    } catch (e) {
      const code = e && (e.code || (e.data && e.data.code));
      if (e && e.status === 409 && code === 'e2ee_ack_required') {
        // Encryption changed since the thread was loaded: show the acknowledgement instead of an error.
        await loadCurrentThread().catch(() => {});
      } else if (code === 'dm_suspended') {
        const until = e.data && e.data.until ? ` until ${String(e.data.until).replace('T', ' ').slice(0, 16)} UTC` : '';
        setDmSendBlocked(`Your direct messages are suspended${until} after a moderation decision. Your message was not sent.`);
      } else if (e && e.status === 409 && (code === 'e2ee_required' || code === 'e2ee_not_active')) {
        setLoadErr('The encryption status of this conversation changed, so your message was not sent. Check the status above and send it again.');
        await loadCurrentThread().catch(() => {});
      } else {
        setLoadErr(String((e && e.message) || e));
      }
    } finally {
      setSending(false);
    }
  }

  async function acknowledgeE2eeChange(messageId) {
    const tid = String(selectedThreadId || '').trim();
    if (!tid) return;
    const res = await comsApiPost(`/dms/thread/${encodeURIComponent(tid)}/e2ee/ack`, { message_id: messageId });
    const nextThread = normalizeThread(safeObj(res && res.thread), serverMemberNameMap);
    setThread(nextThread);
    setThreads((prev) => safeArray(prev).map((item) => (String(item.id) === tid ? { ...item, e2ee: nextThread.e2ee } : item)));
  }

  // Sends one sticker on its own (the draft stays). Resolves true when the server accepted it; the server
  // checks the sender's personal sticker access (server packs do not apply in DMs).
  async function sendDmSticker(token) {
    const tid = String(selectedThreadId || '').trim();
    if (!tid || sending || !canType || e2eeActive) return false;
    markEngaged(`d:${tid}`);
    try {
      setSending(true);
      setLoadErr('');
      const res = await comsApiPost(`/dms/thread/${encodeURIComponent(tid)}/messages`, { content: token });
      const next = normalizeMessage(safeObj(res && res.message), serverMemberNameMap);
      setMessages((prev) => mergeDmMessageRows(prev, [next]));
      await Promise.all([refreshLists(), loadCurrentThread()]);
      requestAnimationFrame(() => {
        stickToBottomRef.current = true;
        maybeAutoScroll();
      });
      return true;
    } catch (e) {
      setLoadErr(String((e && e.message) || e));
      return false;
    } finally {
      setSending(false);
    }
  }

  function canModerateDmMessage(rawMsg) {
    const m = safeObj(rawMsg);
    const dm = safeObj(m._dm);
    return !!(dm.can_edit || dm.can_delete);
  }

  function isDmMessageDeleted(rawMsg) {
    const v = rawMsg && rawMsg.deleted_at;
    return v != null && String(v).trim() !== '';
  }

  function isDmMessageEdited(rawMsg) {
    const v = rawMsg && rawMsg.edited_at;
    return v != null && String(v).trim() !== '';
  }

  function beginEditMessage(rawMsg) {
    const m = safeObj(rawMsg);
    if (!canModerateDmMessage(m)) return;
    if (isDmMessageDeleted(m)) return;
    const id = String(m.id || '').trim();
    if (!id) return;

    setMessageActionErr('');
    setEditingMessageId(id);
    setEditDraft(String(m.body || ''));
    setHoveredMessageId(id);
  }

  function cancelEditMessage() {
    setEditingMessageId('');
    setEditDraft('');
    setMessageActionErr('');
  }

  async function saveEditMessage(messageId) {
    const id = String(messageId || '').trim();
    const nextContent = String(editDraft || '').trim();
    if (!id || !nextContent) return;

    try {
      setEditBusyMessageId(id);
      setMessageActionErr('');

      const res = await comsApiPatch(`/dms/messages/${encodeURIComponent(id)}`, { content: nextContent });
      const updated = normalizeMessage(safeObj(res && res.message), serverMemberNameMap);

      setMessages((prev) => prev.map((msg) => (String(msg.id) === id ? updated : msg)));
      setEditingMessageId('');
      setEditDraft('');
    } catch (e) {
      setMessageActionErr(String((e && e.message) || e));
    } finally {
      setEditBusyMessageId('');
    }
  }

  async function deleteMessage(rawMsg) {
    const m = safeObj(rawMsg);
    const id = String(m.id || '').trim();
    if (!id) return;
    if (!canModerateDmMessage(m)) return;
    if (isDmMessageDeleted(m)) return;

    try {
      setDeleteBusyMessageId(id);
      setMessageActionErr('');

      await comsApiDelete(`/dms/messages/${encodeURIComponent(id)}`);

      setMessages((prev) => prev.filter((msg) => String(msg.id) !== id));

      if (String(editingMessageId || '') === id) {
        setEditingMessageId('');
        setEditDraft('');
      }

      setHoveredMessageId((prev) => (String(prev || '') === id ? '' : prev));
    } catch (e) {
      setMessageActionErr(String((e && e.message) || e));
    } finally {
      setDeleteBusyMessageId('');
    }
  }

  const dmMessagesRaw = useMemo(() => {
    return safeArray(messages).map((msg) =>
      // Encrypted messages show what this device could decrypt, or a placeholder (never an empty message).
      mapDmMessageToChatRaw(msg.encryption === 'e2ee' && !msg.system_kind ? withEncryptedView(msg, decryptedResult(msg.id), 'content') : msg, serverMemberNameMap)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, serverMemberNameMap, decryptTick]);

  const {
    messages: derivedMessages,
    messageSearchNorm,
    filteredMessages,
    messagesWithGroupMeta,
  } = useChatDerivedMessages({
    messagesRaw: dmMessagesRaw,
    membersNameMap: {},
    myChatLabel: String((user && (user.display_name || user.username || user.name)) || '').trim() || 'You',
    myUserId,
    ownerUserId: 0,
    messageSearch: '',
    canModerateMessage: canModerateDmMessage,
    isMessageEdited: isDmMessageEdited,
    isMessageDeleted: isDmMessageDeleted,
  });

  const lastSeenOwnMessageId = useMemo(() => {
    let found = '';
    for (let i = 0; i < messages.length; i += 1) {
      const item = messages[i];
      if (
        toInt(item && item.sender_user_id, 0) === toInt(myUserId, 0) &&
        !item.is_deleted &&
        item.seen_by_other_user
      ) {
        found = String(item.id || '');
      }
    }
    return found;
  }, [messages, myUserId]);

  const messageActions = useMemo(() => {
    return buildSharedMessageActions({
      quoteMessage,
      beginEditMessage,
      deleteMessage,
      editBusyMessageId,
      deleteBusyMessageId,
      bookmarkMessage: (raw) => saveBookmark('dm_message', raw.id),
      // Encrypted messages are reported by disclosing selected ones from this device (e2ee/EncryptedReportDialog.js).
      reportMessage: (raw) => setE2eeReportId(raw.id),
      canReportMessage: (raw) => raw && raw.encryption === 'e2ee' && !raw.system_kind,
      myUserId,
    });
  }, [quoteMessage, beginEditMessage, deleteMessage, editBusyMessageId, deleteBusyMessageId, myUserId]);

  // What the report dialog can offer: messages this device decrypted itself (with the evidence it needs).
  const reportableDmMessages = useMemo(() => {
    if (e2eeReportId == null) return [];
    return safeArray(messages)
      .filter((m) => m.encryption === 'e2ee' && !m.system_kind)
      .map((m) => {
        const r = decryptedResult(m.id);
        return {
          id: m.id,
          author: m.sender_username,
          text: r && r.ok ? r.body : '',
          ok: !!(r && r.ok),
          ciphertext: m.ciphertext,
          senderDevice: r ? r.senderDevice : '',
          verified: !!(r && r.verified),
          fromBackup: !!(r && r.fromBackup),
          unsignedDevice: !!(r && r.unsignedDevice),
          mine: String(m.sender_user_id) === String(myUserId),
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [e2eeReportId, messages, decryptTick, myUserId]);

  // Search results and bookmarks can open a direct message: load older pages until it is there, then scroll.
  useEffect(() => {
    const tid = String(selectedThreadId || '');
    const tryJump = async () => {
      const p = typeof window !== 'undefined' ? window.__voxPendingDmJump : null;
      if (!p || !tid || String(p.threadId) !== tid || !messages.length) return;
      window.__voxPendingDmJump = null;
      const id = Number(p.messageId);
      let rows = messages;
      for (let page = 0; page < 20 && !rows.some((m) => Number(m && m.id) === id); page += 1) {
        const oldest = rows.reduce((min, m) => (Number(m && m.id) && (!min || Number(m.id) < min) ? Number(m.id) : min), 0);
        if (!oldest) break;
        const res = await comsApiGet(`/dms/thread/${encodeURIComponent(tid)}/messages?before_id=${oldest}&limit=100`).catch(() => null);
        const older = safeArray(res && res.messages).map((item) => normalizeMessage(item, serverMemberNameMap));
        if (!older.length) break;
        rows = mergeDmMessageRows(rows, older);
      }
      if (rows !== messages) setMessages(sortDmMessages(rows));
      let tries = 0;
      const scroll = () => {
        const el = document.querySelector(`[data-message-id="${id}"]`);
        if (el) {
          el.scrollIntoView({ block: 'center' });
          el.classList.add('vxo-flash');
          setTimeout(() => el.classList.remove('vxo-flash'), 1600);
        } else if ((tries += 1) < 25) setTimeout(scroll, 80);
      };
      scroll();
    };
    tryJump();
  }, [selectedThreadId, messages, serverMemberNameMap]);

  const placeholder = useMemo(() => {
    if (!activeThread) return 'Click a friend to message';
    if (e2eePendingAck) return 'Acknowledge the encryption change above to keep messaging';
    if (e2eeActive && e2eeBlocked) {
      return e2eeLocal && e2eeLocal.state === 'pending' ? 'Setting up encryption on this device…' : 'Sending is paused: encryption is not set up on this device';
    }
    if (e2eeActive) return 'Write an end-to-end encrypted message';
    return 'Write a message';
  }, [activeThread, e2eePendingAck, e2eeActive, e2eeBlocked, e2eeLocal]);

  const mobileLeftInnerPad = 8;
  const mobileRightInnerPad = 6;

  const chatRootStyle = isShellMobile
    ? {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        overflow: 'hidden',
      }
    : {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
      };

  const messagesStyle = {
    paddingBottom: 0,
    ...(isShellMobile
      ? {
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          width: '100%',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          paddingLeft: mobileLeftInnerPad,
          paddingRight: mobileRightInnerPad,
          paddingTop: 8,
          paddingBottom: 6,
          boxSizing: 'border-box',
          overflowX: 'hidden',
        }
      : {
          flex: 1,
          minHeight: 0,
        }),
  };

  const inputBarStyle = {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    ...(isShellMobile
      ? {
          paddingLeft: mobileLeftInnerPad,
          paddingRight: mobileRightInnerPad,
          paddingTop: 8,
          paddingBottom: 'max(8px, env(safe-area-inset-bottom, 0px))',
          borderTop: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
          background: 'rgba(var(--vxs-2-rgb, 18,22,32), 0.96)',
          backdropFilter: 'blur(8px)',
          position: 'sticky',
          bottom: 0,
          zIndex: 5,
          boxSizing: 'border-box',
        }
      : {}),
  };

  const avatarWrapperStyle = {
    width: 32,
    height: 32,
    borderRadius: '50%',
    overflow: 'hidden',
    background: 'var(--vxs-1, #111214)',
    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.15 * var(--vx-ov, 1)))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: 16,
    color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.8 * var(--vx-ov, 1)))',
  };

  const mobileComposerCompact = isShellMobile;

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();

      const composed = composeOutgoingMessageBodyWithUploads(draft, pendingQuote, pendingUploads, buildQuoteBlockFromPending);
      const uploadIds = safeArray(pendingUploads).map((item) => toInt(item && item.id, 0)).filter((v) => v > 0);
      if (!composed && !uploadIds.length) return;

      sendMessage({ closeEmojiPicker: () => setShowEmojiPicker(false) }).catch(() => {});
    }
  }

  const onClickSend = () => {
    const composed = composeOutgoingMessageBodyWithUploads(draft, pendingQuote, pendingUploads, buildQuoteBlockFromPending);
    const uploadIds = safeArray(pendingUploads).map((item) => toInt(item && item.id, 0)).filter((v) => v > 0);
    if (!composed && !uploadIds.length) return;

    sendMessage({ closeEmojiPicker: () => setShowEmojiPicker(false) }).catch(() => {});
  };

  const footerRenderer = useCallback((m) => {
    if (!m) return null;
    if (String(m.id || '') !== String(lastSeenOwnMessageId || '')) return null;
    if (String((m.userId != null ? m.userId : '') || '') !== String(myUserId || '')) return null;
    if (m.raw && isDmMessageDeleted(m.raw)) return null;

    return (
      <div style={{ fontSize: 11, color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.5 * var(--vx-ov, 1)))', paddingTop: 2, paddingBottom: 6 }}>
        Seen
      </div>
    );
  }, [lastSeenOwnMessageId, myUserId]);

  async function acceptFriend(userId) {
    try {
      setLoadErr('');
      await comsApiPost(`/friends/${encodeURIComponent(String(userId))}/accept`, {});
      await refreshLists();
    } catch (e) {
      setLoadErr(String((e && e.message) || e));
    }
  }

  async function declineFriend(userId) {
    try {
      setLoadErr('');
      await comsApiPost(`/friends/${encodeURIComponent(String(userId))}/decline`, {});
      await refreshLists();
    } catch (e) {
      setLoadErr(String((e && e.message) || e));
    }
  }

  if (socialSection === 'friends') {
    const viewTitle = 'Friends';
    const viewSub = `${friends.length} friend${friends.length === 1 ? '' : 's'}`;

    return (
      <div
        className="coms-view"
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          height: '100%',
          padding: 16,
          boxSizing: 'border-box',
          gap: 12,
        }}
      >
        <div style={panelStyle()}>
          {sectionHeader(viewTitle, viewSub)}
          {loadErr ? <div style={{ fontSize: 13, color: '#ffb4b4' }}>{loadErr}</div> : null}
        </div>

        <div style={{ ...panelStyle(), flex: 1, overflowY: 'auto' }}>
          <div style={{ display: 'grid', gap: 8 }}>
            {friends.length ? friends.map((friend) => (
              <FriendRow key={friend.other_user_id} friend={friend} onOpenChat={openFriendChat} />
            )) : (
              <p style={textStyle()}>No friends yet.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (socialSection === 'pending') {
    const viewTitle = 'Pending';
    const viewSub = `${incoming.length} incoming · ${outgoing.length} outgoing`;

    return (
      <div
        className="coms-view"
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          height: '100%',
          padding: 16,
          boxSizing: 'border-box',
          gap: 12,
        }}
      >
        <div style={panelStyle()}>
          {sectionHeader(viewTitle, viewSub)}
          {loadErr ? <div style={{ fontSize: 13, color: '#ffb4b4' }}>{loadErr}</div> : null}
        </div>

        <div style={{ ...panelStyle(), flex: 1, overflowY: 'auto', display: 'grid', gap: 16 }}>
          <div style={{ display: 'grid', gap: 8 }}>
            <h3 style={{ ...titleStyle(), fontSize: 15 }}>Incoming</h3>
            {incoming.length ? incoming.map((item) => (
              <PendingRow
                key={`in_${item.id}`}
                item={item}
                type="incoming"
                onAccept={acceptFriend}
                onDecline={declineFriend}
              />
            )) : (
              <p style={textStyle()}>No incoming friend requests.</p>
            )}
          </div>

          <div style={{ display: 'grid', gap: 8 }}>
            <h3 style={{ ...titleStyle(), fontSize: 15 }}>Outgoing</h3>
            {outgoing.length ? outgoing.map((item) => (
              <PendingRow
                key={`out_${item.id}`}
                item={item}
                type="outgoing"
                onAccept={acceptFriend}
                onDecline={declineFriend}
              />
            )) : (
              <p style={textStyle()}>No outgoing friend requests.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="coms-view" style={chatRootStyle} data-coms-chat-mobile={isShellMobile ? '1' : '0'}>
      <ChatViewScrollbarStyle />

      {activeThread && selectedThreadId ? (
        <div className="vxo-dm-tools">
          <button type="button" className="vx-btn small" onClick={() => saveBookmark('dm', selectedThreadId)} data-testid="dm-bookmark">
            🔖 Save conversation
          </button>
          <button type="button" className="vx-btn small" onClick={() => openArea('search', { scope: 'dm', dm: selectedThreadId })}>
            🔎 Search conversation
          </button>
        </div>
      ) : null}

      {activeThread && selectedThreadId ? (
        <DmEncryptionBar
          e2ee={threadE2ee}
          local={e2eeLocal}
          otherName={activeThread.username}
          onAcknowledge={acknowledgeE2eeChange}
          onRetry={() => retryThread(selectedThreadId, threadE2ee)}
          contact={e2eeContact}
          onVerify={async () => {
            await verifyContact(threadE2ee.members[0], threadE2ee.members[1]);
            await retryThread(selectedThreadId, threadE2ee);
          }}
          onAcceptIdentity={async () => {
            await acceptContactChange(threadE2ee.members[0], threadE2ee.members[1]);
            await retryThread(selectedThreadId, threadE2ee);
          }}
        />
      ) : null}

      <div
        className="coms-messages"
        ref={scrollerRef}
        style={messagesStyle}
        onScroll={handleScrollerScroll}
      >
        {!activeThread && !loadErr ? (
          <div
            ref={messagesContentRef}
            style={{
              minHeight: '100%',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              padding: '18px 16px',
              boxSizing: 'border-box',
              color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.62 * var(--vx-ov, 1)))',
              fontSize: 14,
            }}
          >
            No messages yet
          </div>
        ) : (
          <ChatMessageList
            messagesWithGroupMeta={messagesWithGroupMeta}
            messages={derivedMessages}
            filteredMessages={filteredMessages}
            loading={false}
            loadErr={loadErr}
            membersLoaded
            messageActionErr={messageActionErr}
            messageSearchNorm={messageSearchNorm}
            messageSearch=""
            isShellMobile={isShellMobile}
            avatarWrapperStyle={avatarWrapperStyle}
            messagesContentRef={messagesContentRef}
            editingMessageId={editingMessageId}
            editDraft={editDraft}
            editBusyMessageId={editBusyMessageId}
            deleteBusyMessageId={deleteBusyMessageId}
            hoveredMessageId={hoveredMessageId}
            setHoveredMessageId={setHoveredMessageId}
            setEditDraft={setEditDraft}
            beginEditMessage={beginEditMessage}
            deleteMessage={deleteMessage}
            cancelEditMessage={cancelEditMessage}
            saveEditMessage={saveEditMessage}
            quoteMessage={quoteMessage}
            messageActions={messageActions}
            footerRenderer={footerRenderer}
          />
        )}
      </div>

      {activeThread ? <ChatComposer
        isShellMobile={isShellMobile}
        canType={canType}
        showEmojiPicker={showEmojiPicker}
        setShowEmojiPicker={setShowEmojiPicker}
        emojiButtonRef={emojiButtonRef}
        emojiPopoverRef={emojiPopoverRef}
        inputRef={inputRef}
        draft={draft}
        setDraft={setDraft}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        mobileComposerCompact={mobileComposerCompact}
        sending={sending}
        send={onClickSend}
        insertIntoDraftAtCursor={insertIntoDraftAtCursor}
        inputBarStyle={inputBarStyle}
        pendingQuote={pendingQuote}
        clearPendingQuote={clearPendingQuote}
        pendingUploads={pendingUploads}
        removePendingUpload={removePendingUpload}
        onPickFiles={e2eeActive ? undefined : handlePickFiles}
        uploading={uploading}
        clearPendingUploads={clearPendingUploads}
        enableFontPicker={false}
        serverKey=""
        onSendSticker={sendDmSticker}
        canSendStickers={!e2eeActive}
        canSendServerStickers={false}
        canSendExternalStickers
        onOpenStickerManager={() => {
          try {
            window.__voxPendingSettingsTab = 'Stickers';
            window.dispatchEvent(new CustomEvent('vox-open-settings', { detail: { tab: 'Stickers' } }));
          } catch {}
        }}
      /> : null}
      {dmSendBlocked && activeThread ? (
        <div className="vx-e2ee-wrap">
          <div className="vx-e2ee-ack" role="alert" data-testid="dm-send-blocked">
            {dmSendBlocked}
          </div>
        </div>
      ) : null}
      {e2eeReportId != null && selectedThreadId ? (
        <EncryptedReportDialog
          scope="dm"
          conversationId={selectedThreadId}
          messages={reportableDmMessages}
          initialId={e2eeReportId}
          onClose={() => setE2eeReportId(null)}
        />
      ) : null}
    </div>
  );
}